import { useEffect, useState, useRef, useCallback } from "react";
import { BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import { ToastContainer } from "react-toastify";
import "./styles/toastStyles.css";
import AppRoutes from "./routes";
import { NavbarProvider } from "./context/NavbarContext";
import { checkUserSession } from "./redux/actions/authActions";
import ChatAI from "./views/ChatAI";
import ContextMenu from "./components/ContextMenu";
import RightClickHandler from "./components/RightClickHandler";
import { usePostHogTracking } from "./hooks/usePostHogTracking";
import rightClickInfoConfig from "./config/rightClickInfoConfig";
import SessionExpiredPopup from "./components/SessionExpiredPopup";
import TrialBanner from "./components/TrialBanner";
import { env } from "./env";

function AppContent() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const posthog = usePostHog();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [userSessionChecked, setUserSessionChecked] = useState(false);
  const effectRan = useRef(false);

  const [showAssistant, setShowAssistant] = useState(() => {
    const saved = localStorage.getItem('showAssistant');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Initialize PostHog tracking (hooks must be called unconditionally)
  // const { isTracking } = usePostHogTracking();

  const [contextMenuData, setContextMenuData] = useState({
    visible: false,
    x: 0,
    y: 0,
    feature: '',
  });

  useEffect(() => {
    localStorage.setItem('showAssistant', JSON.stringify(showAssistant));
  }, [showAssistant]);

  useEffect(() => {
    const handleToggleAssistant = () => {
      setShowAssistant(prev => !prev);
    };
    window.addEventListener('toggleAssistant', handleToggleAssistant);
    return () => window.removeEventListener('toggleAssistant', handleToggleAssistant);
  }, []);

  const handleNotificationAction = useCallback((e) => {
    const { action } = e.detail || {};
    if (action.label === "route" && action.action) {
      navigate(action.action);
    } else if (action.action) {
      window.open(action.action, '_blank');
    }
  }, [navigate]);

  useEffect(() => {
    window.addEventListener("notificationAction", handleNotificationAction);
    return () =>
      window.removeEventListener(
        "notificationAction",
        handleNotificationAction
      );
  }, [handleNotificationAction]);

  useEffect(() => {
    const handleContextMenu = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      e.preventDefault();
      
      // Traverse up the DOM tree to find the nearest data-feature attribute
      let feature = null;
      let element = e.target;
      while (element && element !== document.body) {
        const attr = element.getAttribute?.('data-feature');
        if (attr) {
          feature = attr;
          break;
        }
        element = element.parentElement;
      }
      
      setContextMenuData({
        visible: true,
        x: e.pageX,
        y: e.pageY,
        feature: feature || null,
      });
    };

    const handleClick = () => {
      if (contextMenuData.visible) {
        setContextMenuData(prev => ({ ...prev, visible: false }));
      }
    };

    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("click", handleClick);
    };
  }, [contextMenuData.visible]);

  useEffect(() => {
    if (effectRan.current === false) {
      const checkSession = async () => {
        try {
          await dispatch(checkUserSession());
        } catch (error) {
          console.error("Error checking user session:", error);
        } finally {
          setUserSessionChecked(true); // Set to true after session check completes
        }
      };
      checkSession();

      return () => {
        effectRan.current = true;
      };
    }
  }, [dispatch]);

  // PostHog tracking is now handled by usePostHogTracking hook
  // The commented code below has been replaced by the hook

  // Hide ChatAI on login, register, and chat pages
  const hideChatAI =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/chat";


  return (
    <NavbarProvider>
      <TrialBanner />
      <RightClickHandler />
      <AppRoutes />
      <SessionExpiredPopup />
      {!hideChatAI && showAssistant && isAuthenticated && (
        <ChatAI />
      )}
      {contextMenuData.visible && (
        <ContextMenu
          x={contextMenuData.x}
          y={contextMenuData.y}
          featureInfo={contextMenuData.feature}
          onClose={() => setContextMenuData({ ...contextMenuData, visible: false })}
          showTourOption={!!rightClickInfoConfig[location.pathname]}
          onShowTour={() => {
            const eventName = `showTour-${location.pathname.replace(/\//g, '_')}`;
            window.dispatchEvent(new CustomEvent(eventName, { detail: { forceShow: true } }));
            setContextMenuData(prev => ({ ...prev, visible: false }));
          }}
        />
      )}
      <ToastContainer
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 50000 }}
        theme="light"
        className="toast-container"
        enableMultiContainer={false}
      />
    </NavbarProvider>
  );
}

function App() {
  if (env.IS_MAINTENANCE === 'true') {
    return (
      <div className="min-h-screen relative flex flex-col items-center justify-center p-4 font-sans bg-slate-50 overflow-hidden">
        {/* Animated background blobs */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-slate-200 blur-3xl opacity-50 animate-pulse-slow mix-blend-multiply"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-gray-200 blur-3xl opacity-50 animate-pulse-slow mix-blend-multiply" style={{ animationDelay: '2s' }}></div>

        <div className="relative bg-white/80 border border-white backdrop-blur-xl p-8 md:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] max-w-lg w-full text-center z-10 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(71,85,105,0.1)]">
          <div className="w-24 h-24 bg-gradient-to-tr from-slate-50 to-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner ring-4 ring-white animate-fade-in-up">
            <svg className="w-12 h-12 animate-spin-slow text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-montserrat font-bold mb-4 tracking-tight text-slate-800 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Under Maintenance
          </h1>
          <p className="text-slate-600 font-sans leading-relaxed mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            We are currently performing scheduled maintenance to improve your experience. 
            <br /><br />
            Our systems will be back online shortly. Thank you for your patience!
          </p>
          <div className="relative inline-flex group animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="absolute transition-all duration-1000 opacity-70 -inset-px bg-slate-300 rounded-full blur-lg group-hover:opacity-100 group-hover:-inset-1 group-hover:duration-200 animate-pulse-slow"></div>
            <div className="relative inline-flex border border-transparent items-center justify-center px-8 py-3 text-sm font-montserrat font-semibold text-white transition-all duration-200 bg-slate-800 hover:bg-slate-900 rounded-full shadow-md">
              System update in progress
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent />
    </Router>
    //  <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
    //   <div className="bg-white p-8 md:p-12 rounded-2xl shadow-xl max-w-lg w-full text-center border border-gray-100">
    //     <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
    //       <svg className="w-10 h-10 animate-spin-slow" style={{ animationDuration: '3s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    //       </svg>
    //     </div>
    //     <h1 className="text-3xl font-bold text-gray-900 mb-4">Under Maintenance</h1>
    //     <p className="text-gray-600 leading-relaxed mb-8">
    //       We are currently performing scheduled maintenance to improve your experience. 
    //       <br /><br />
    //       Our platform will be back online shortly. Thank you for your patience!
    //     </p>
    //     <div className="inline-block border border-blue-100 bg-blue-50 text-blue-700 px-6 py-3 rounded-lg text-sm font-medium">
    //       Our systems are temporarily paused
    //     </div>
    //   </div>
    // </div>
  );
}

export default App;
